/**
 * Company Directory — React Query Hooks
 *
 * Browse, search, and claim startups from the pre-populated company directory.
 */

import { escapeLikePattern } from '@/lib/sql/escape-like';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase, isSupabaseConfigured } from './client';
import { claimCompany } from './edge-functions';
import type { Database } from './database.types';
import { LIFECYCLE_LABEL, isLifecycleStatus, type LifecycleStatus } from '@/lib/company/lifecycle';
import { claimRefusalFor } from '@/lib/company/claim-refusals';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type CompanyDirectoryRow = Database['public']['Tables']['company_directory']['Row'];

export interface CompanyDirectoryEntry extends CompanyDirectoryRow {}

/**
 * Every value `company_directory.status` can hold (CHECK in
 * 20261019100000_company_lifecycle.sql). The three lifecycle values are kept in
 * step with `startup_lifecycle` by trigger (119-01).
 */
export type DirectoryStatus =
  | 'active'
  | 'claimed'
  | 'inactive'
  | 'duplicate'
  | LifecycleStatus;

export function isLifecycleDirectoryStatus(status: string | null | undefined): status is LifecycleStatus {
  return isLifecycleStatus(status);
}

/** The badge text for a directory row's status. */
export function directoryStatusLabel(status: string | null | undefined): string {
  if (isLifecycleStatus(status)) return LIFECYCLE_LABEL[status].toUpperCase();
  return status === 'claimed' ? 'CLAIMED' : 'UNCLAIMED';
}



export interface CompanyDirectoryFilters {
  search?: string;
  industry?: string;
  canton?: string;
  stage?: string;
  hasWebsite?: boolean;
  limit?: number;
  offset?: number;
}

// ---------------------------------------------------------------------------
// Query Keys
// ---------------------------------------------------------------------------

/**
 * The statuses the directory LISTS. The header count reads the same constant:
 * it used to count `status = 'active'` only, so a directory listing a claimed
 * company said "0 startups in the directory" (119-07 UAT). Closed companies are
 * listed separately (closed_company_directory) and not counted here.
 */
export const LISTED_DIRECTORY_STATUSES = ['active', 'claimed'] as const;

export const directoryKeys = {
  all: ['companyDirectory'] as const,
  list: (filters: CompanyDirectoryFilters) =>
    ['companyDirectory', 'list', filters] as const,
  item: (id: string) => ['companyDirectory', 'item', id] as const,
  count: () => ['companyDirectory', 'count'] as const,
  autocomplete: (query: string) =>
    ['companyDirectory', 'autocomplete', query] as const,
};

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Browse the company directory with search, filters, and pagination.
 */
export function useCompanyDirectory(filters: CompanyDirectoryFilters) {
  return useQuery({
    queryKey: directoryKeys.list(filters),
    queryFn: async () => {
      if (!isSupabaseConfigured()) return [];

      let query = supabase
        .from('company_directory')
        .select('*')
        .in('status', [...LISTED_DIRECTORY_STATUSES])
        .order('name', { ascending: true });

      // Full-text search
      if (filters.search && filters.search.length >= 2) {
        query = query.or(
          `name.ilike.%${filters.search}%,industry.ilike.%${filters.search}%,sector.ilike.%${filters.search}%,description.ilike.%${filters.search}%,canton.ilike.%${filters.search}%,city.ilike.%${filters.search}%`
        );
      }

      // Filters
      if (filters.industry) {
        query = query.or(
          `industry.ilike.%${filters.industry}%,sector.ilike.%${filters.industry}%`
        );
      }
      if (filters.canton) {
        query = query.eq('canton', filters.canton);
      }
      if (filters.stage) {
        query = query.eq('stage', filters.stage);
      }
      if (filters.hasWebsite) {
        query = query.not('website', 'is', null);
      }

      // Pagination
      const limit = filters.limit ?? 50;
      const offset = filters.offset ?? 0;
      query = query.range(offset, offset + limit - 1);

      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as CompanyDirectoryEntry[];
    },
    enabled: isSupabaseConfigured(),
    staleTime: 5 * 60 * 1000, // 5 min — directory data is relatively stable
  });
}

/**
 * Autocomplete search — returns top matches for a search query.
 * Optimized for speed: only fetches name, id, industry, canton, logo_url.
 */
export function useDirectoryAutocomplete(query: string) {
  return useQuery({
    queryKey: directoryKeys.autocomplete(query),
    queryFn: async () => {
      if (!isSupabaseConfigured() || !query || query.length < 2) return [];

      const { data, error } = await supabase
        .from('company_directory')
        .select('id, name, industry, canton, city, logo_url, status')
        .eq('status', 'active')
        .ilike('name', `%${escapeLikePattern(query)}%`)
        .order('name', { ascending: true })
        .limit(10);

      if (error) throw error;
      return (data ?? []) as Array<
        Pick<
          CompanyDirectoryEntry,
          'id' | 'name' | 'industry' | 'canton' | 'city' | 'logo_url' | 'status'
        >
      >;
    },
    enabled: isSupabaseConfigured() && query.length >= 2,
  });
}

/**
 * Fetch a single company directory entry by ID.
 */
export function useCompanyDirectoryItem(id: string | undefined) {
  return useQuery({
    queryKey: directoryKeys.item(id ?? ''),
    queryFn: async () => {
      if (!isSupabaseConfigured() || !id) return null;

      const { data, error } = await supabase
        .from('company_directory')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      return data as CompanyDirectoryEntry;
    },
    enabled: !!id && isSupabaseConfigured(),
  });
}

/**
 * Get total count of active directory entries.
 */
export function useCompanyDirectoryCount() {
  return useQuery({
    queryKey: directoryKeys.count(),
    queryFn: async () => {
      if (!isSupabaseConfigured()) return 0;

      const { count, error } = await supabase
        .from('company_directory')
        .select('*', { count: 'exact', head: true })
        .in('status', [...LISTED_DIRECTORY_STATUSES]);

      if (error) throw error;
      return count ?? 0;
    },
    enabled: isSupabaseConfigured(),
  });
}

/**
 * Claim a company from the directory.
 *
 * Phase 116.2: gated on a `company_claim_verifications.status='verified'`
 * row matching `(verificationId, directoryId, userId)`. Without that
 * record, the claim is rejected — auto-promoted directory rows would
 * otherwise be open to claim-jacking by any authenticated user.
 *
 * 118-10: the claim itself is ONE call to `onboarding-claim { directoryId }`,
 * which grants ownership as an active `team_members` owner row and — with the
 * service role, after re-checking the verification — creates the page for an
 * unpaired seed row, links the directory row to it and marks it claimed.
 * This hook used to do those writes itself: it set `startup_profiles.user_id`
 * (so erasing the founder CASCADE-deleted the company) and re-pointed
 * `company_directory` through a policy anyone could use. It writes neither
 * table now; ownership has one path.
 *
 * The client checks below (verification row, directory availability, an
 * existing company of the caller's own) only give a clear early error — the
 * server decides.
 */
export function useClaimCompany() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      directoryId,
      userId,
      verificationId,
    }: {
      directoryId: string;
      userId: string;
      /**
       * Phase 116.2: id of the verified `company_claim_verifications`
       * row. Obtained from the `verify-company-claim` edge function's
       * `submit_email_code` action. The hook re-verifies this row
       * matches `(directory_id, requested_by_user_id, status='verified')`
       * client-side; RLS guarantees the user can only see their own.
       */
      verificationId: string;
    }) => {
      if (!isSupabaseConfigured()) throw new Error('Supabase not configured');

      // 0. Verify the claim is backed by a verified verification record
      //    for this user + directory pair. RLS on
      //    `company_claim_verifications` already constrains the SELECT to
      //    rows where `requested_by_user_id = auth.uid()`, so this is
      //    defense-in-depth + clear user-facing error.
      const { data: verification, error: verifyFetchError } = await supabase
        .from('company_claim_verifications')
        .select('id, directory_id, status, requested_by_user_id')
        .eq('id', verificationId)
        .eq('directory_id', directoryId)
        .eq('requested_by_user_id', userId)
        .eq('status', 'verified')
        .maybeSingle();

      if (verifyFetchError || !verification) {
        throw new Error(
          'Claim verification not found or has expired. Please re-verify your company email.',
        );
      }

      // 1. Fetch the directory entry
      const { data: company, error: fetchError } = await supabase
        .from('company_directory')
        .select('*')
        .eq('id', directoryId)
        .eq('status', 'active')
        .is('claimed_by', null)
        .single();

      // A miss is NOT final. A closed / acquired / bankrupt company's row is
      // invisible to clients (RLS shows only active / claimed), so this check
      // can never see it — throwing here would mask the server's
      // `company_closed` answer with "not found". The server decides; a miss
      // only changes the fallback message below.
      const precheckMissed = !!fetchError || !company;

      // 2. Check whether the founder already has a startup_profile, so a second
      //    claim fails fast with a clear error.
      //
      //    Resolved via ACTIVE TEAM MEMBERSHIP — the only definition of
      //    ownership since 2026-07-21. This used to read
      //    `startup_profiles.user_id` and lean on that column's UNIQUE
      //    constraint; the column was emptied on 2026-08-04, so BOTH halves of
      //    that guard went quiet at once — the lookup always found nothing and
      //    the constraint no longer applied to a NULL. A founder could claim a
      //    second company and end up owning two profiles, with nothing raising.
      const { data: ownMemberships } = await supabase
        .from('team_members')
        .select('team_id')
        .eq('user_id', userId)
        .eq('status', 'active');
      const ownTeamIds = (ownMemberships ?? []).map((r: { team_id: string }) => r.team_id);
      const { data: ownedStartups } = ownTeamIds.length > 0
        ? await supabase.from('teams').select('startup_id').in('id', ownTeamIds)
        : { data: [] as Array<{ startup_id: string | null }> };
      const existingOwnProfile = (ownedStartups ?? []).find((r) => r.startup_id !== null)
        ? { id: (ownedStartups ?? []).find((r) => r.startup_id !== null)!.startup_id as string }
        : null;

      if (existingOwnProfile) {
        throw new Error(
          'You already have a startup profile. Please contact support to merge this claim with your existing profile.',
        );
      }

      // 3. Claim through the ownership bridge — the only path that writes.
      const { data: claimed, error: claimError, code } = await claimCompany({ directoryId });
      if (claimError || !claimed) {
        if (code === 'ALREADY_OWNED') {
          throw new Error('This company has already been claimed by another account.');
        }
        // 119-03 lifecycle refusals (409): branch on the code, never the text.
        // A typed error: a plain Error's text would not survive
        // handleError → getUserMessage.
        const refusal = claimRefusalFor(code);
        if (refusal) throw refusal;
        if (precheckMissed) throw new Error('Company not found or already claimed');
        throw new Error(claimError ?? 'Could not claim this company. Please try again.');
      }

      return { profileId: claimed.startupId, directoryId };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: directoryKeys.all });
    },
  });
}
